
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { confirmMembershipPayment } from "@/lib/supabase/membership/confirmMembershipPayment";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [membershipType, setMembershipType] = useState<string | null>(null);
  const sessionCheckComplete = useRef(false);
  const maxRetries = 5; // Increase max retries
  
  // Extract session ID from multiple sources with detailed logging
  const getSessionId = useCallback(() => {
    // 1. Try from URL search params (standard URL format)
    const sessionIdFromUrl = searchParams.get("session_id");
    
    // 2. Try from hash params (hash-based routing)
    let sessionIdFromHash = null;
    const hashParams = location.hash.split('?')[1];
    if (hashParams) {
      const hashParamsObj = new URLSearchParams(hashParams);
      sessionIdFromHash = hashParamsObj.get("session_id");
    }
    
    // 3. Try from localStorage (for when redirected without params)
    const sessionIdFromStorage = localStorage.getItem("stripe_session_id");
    
    const finalSessionId = sessionIdFromUrl || sessionIdFromHash || sessionIdFromStorage;
    
    console.log("🔍 Session ID sources:", { 
      fromUrl: sessionIdFromUrl, 
      fromHash: sessionIdFromHash, 
      fromStorage: sessionIdFromStorage,
      final: finalSessionId
    });
    
    return finalSessionId;
  }, [searchParams, location]);
  
  const sessionId = getSessionId();
  
  // Log visibility state for debugging
  useEffect(() => {
    if (document.visibilityState) {
      console.log(`Document visibility: ${document.visibilityState}`);
    }
    
    // Report page load state
    console.log(`Payment success page loaded. Session ID: ${sessionId}`);
    
    // Store session ID in case of page refresh or redirect issues
    if (sessionId) {
      console.log(`Storing session ID in localStorage: ${sessionId}`);
      localStorage.setItem("stripe_session_id", sessionId);
    }

    // Set up clean reload in case of issues (only once)
    if (!sessionCheckComplete.current && !sessionId) {
      console.log("No session ID found - waiting briefly to see if it arrives");
      const timer = setTimeout(() => {
        // Check one more time before showing error
        const finalSessionId = getSessionId();
        if (!finalSessionId) {
          console.log("Final session ID check failed - showing error");
          setError("Session ID introuvable. Veuillez contacter notre support.");
          setIsLoading(false);
        }
      }, 3000); // Longer wait time

      return () => {
        clearTimeout(timer);
        sessionCheckComplete.current = true;
      };
    }
  }, [sessionId, getSessionId]);

  // Process payment function with better error handling
  const processPayment = useCallback(async (paymentSessionId: string) => {
    if (!paymentSessionId) {
      console.error("No session ID available");
      setError("Session ID introuvable. Veuillez vérifier vos emails ou contacter le support.");
      setIsLoading(false);
      return;
    }

    try {
      console.log(`Confirming payment with session ID: ${paymentSessionId}`);
      const result = await confirmMembershipPayment(paymentSessionId, paymentSessionId);
      
      if (result.success) {
        console.log("Payment confirmation successful:", result);
        setSuccess(true);
        setIsLoading(false);
        
        // Try to get membership type for customized messaging
        try {
          if (result.membershipId) {
            const { data } = await supabase
              .from("memberships")
              .select("membership_type")
              .eq("id", result.membershipId)
              .maybeSingle();
            
            if (data) {
              setMembershipType(data.membership_type);
              console.log("Retrieved membership type:", data.membership_type);
            }
          }
        } catch (err) {
          console.error("Could not fetch membership type:", err);
        }
        
        // Clean up storage
        localStorage.removeItem("stripe_session_id");
        
        const isDonor = membershipType === "donor";
        
        toast({
          title: isDonor ? "Don confirmé" : "Adhésion confirmée",
          description: isDonor 
            ? "Votre don a été enregistré avec succès. Merci pour votre soutien !"
            : "Votre adhésion a été enregistrée avec succès. Un email avec votre CERFA et votre Charte personnalisée a été envoyé.",
        });
        
        // Set timeout for redirect - increase to give more time for email sending
        setTimeout(() => {
          navigate("/charte-republicaine");
        }, 8000); // Augmenté à 8 secondes pour laisser plus de temps pour l'envoi des emails
      } else {
        throw new Error("Failed to confirm payment");
      }
    } catch (err) {
      console.error("Error during payment confirmation:", err);
      const errorMessage = err instanceof Error ? err.message : "An error occurred";
      setError(errorMessage);
      
      // Retry for specific errors, with backoff, more attempts
      if ((errorMessage.includes("non trouvée") || errorMessage.includes("Adhésion non trouvée")) && retryCount < maxRetries) {
        console.log(`Membership not found yet, retrying (${retryCount + 1}/${maxRetries}) in ${(retryCount + 1) * 2} seconds...`);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          setIsLoading(true);
          setError(null);
        }, (retryCount + 1) * 2000); // Exponential backoff
      } else {
        setIsLoading(false);
      }
    }
  }, [navigate, retryCount, toast, membershipType, maxRetries]);

  // Trigger payment processing when component loads or retries
  useEffect(() => {
    if (isLoading && sessionId) {
      processPayment(sessionId);
    } else if (isLoading && !sessionId) {
      console.error("No session ID found in URL or storage");
      setError("Session ID introuvable. Veuillez contacter notre support.");
      setIsLoading(false);
    }
  }, [sessionId, isLoading, processPayment]);

  // Déterminer si l'utilisateur est un donateur ou un adhérent
  const isDonor = membershipType === "donor";
  const actionType = isDonor ? "don" : "adhésion";

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <div className="flex-grow py-12 pt-36 bg-white">
        <div className="container max-w-xl mx-auto">
          <Card className="border-2">
            <CardContent className="pt-6">
              {isLoading ? (
                <div className="text-center p-10">
                  <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto" />
                  <p className="mt-4 text-xl">Confirmation de votre paiement en cours...</p>
                  {retryCount > 0 && (
                    <p className="mt-2 text-sm text-gray-500">
                      Tentative {retryCount}/{maxRetries} - Veuillez patienter...
                    </p>
                  )}
                  <p className="mt-4 text-sm text-gray-500">
                    Nous préparons votre CERFA et votre Charte personnalisée...
                  </p>
                </div>
              ) : error ? (
                <div className="text-center p-10">
                  <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <div className="text-red-500 text-xl mb-4">Erreur</div>
                  <p className="mb-6">{error}</p>
                  <div className="space-y-3">
                    <Button onClick={() => navigate("/adhesion")} variant="outline" className="w-full">
                      Retourner à la page d'adhésion
                    </Button>
                    <Button onClick={() => {
                      localStorage.removeItem("stripe_session_id");
                      window.location.reload();
                    }} className="w-full">
                      Réessayer
                    </Button>
                  </div>
                  <p className="mt-6 text-sm text-gray-600">
                    Si le problème persiste, veuillez nous contacter à contact@ledlr.org
                  </p>
                </div>
              ) : (
                <div className="text-center p-10">
                  <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
                  <h1 className="text-3xl font-bold mb-2">Paiement réussi !</h1>
                  <p className="text-xl mb-8">
                    {isDonor 
                      ? "Merci pour votre don aux Enfants de la République" 
                      : "Merci pour votre adhésion au Club des Enfants de la République"}
                  </p>
                  <p className="text-gray-600 mb-4">
                    Vous allez recevoir un email contenant votre CERFA fiscal et votre Charte Républicaine personnalisée.
                  </p>
                  <p className="text-gray-600 mb-8">
                    Vous allez être redirigé vers la charte républicaine dans quelques instants...
                  </p>
                  <Button onClick={() => navigate("/charte-republicaine")} className="gap-2">
                    Accéder à la charte maintenant
                    <ArrowRight size={16} />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
